import { useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Modal } from '~/shared/components/modal/Modal';
import { Button } from '~/shared/components/buttons/Button';
import { Subtitle } from '~/shared/components/text/Subtitle';
import { FooterButtonContainer } from '~/shared/forms/buttons/FooterButtonContainer';
import { ButtonGroup } from '~/shared/forms/controls/base-controls/ButtonGroup';
import { Checkbox } from '~/shared/forms/controls/base-controls/Checkbox';
import { SearchInput } from '~/shared/forms/controls/base-controls/text-controls/SearchInput';
import { cn } from '~/shared/components/utils';
import { COUNTY_LABELS, PITTSBURGH_CITY_ZIPS, ZIP_CODES, ZIP_CODES_BY_ZIP, type ZipCodeEntry } from './zipCodeData';

type PickerMode = 'pick' | 'add';

const MODE_OPTIONS: { id: PickerMode; label: string }[] = [
	{ id: 'pick', label: 'Tap ZIPs' },
	{ id: 'add', label: 'Draw to add' }
];

const MODE_HINTS: Record<PickerMode, string> = {
	pick: 'Tap any dot to add or remove that ZIP.',
	add: 'Draw a loop around the ZIPs you want to add.'
};

const PAD = 26;
const WIDTH = 640;
const HEIGHT = 560;

const lats = ZIP_CODES.map(x => x.lat);
const lons = ZIP_CODES.map(x => x.lon);
const latMin = Math.min(...lats);
const latMax = Math.max(...lats);
const lonMin = Math.min(...lons);
const lonMax = Math.max(...lons);
const scaleX = (WIDTH - 2 * PAD) / (lonMax - lonMin);
const scaleY = (HEIGHT - 2 * PAD) / (latMax - latMin);

const toX = (lon: number) => PAD + (lon - lonMin) * scaleX;
const toY = (lat: number) => PAD + (latMax - lat) * scaleY;

function milesBetween(a: ZipCodeEntry, b: ZipCodeEntry) {
	const radius = 3958.8;
	const rad = Math.PI / 180;
	const dLat = (b.lat - a.lat) * rad;
	const dLon = (b.lon - a.lon) * rad;
	const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLon / 2) ** 2;
	return 2 * radius * Math.asin(Math.sqrt(h));
}

function isInsidePolygon(x: number, y: number, points: { x: number; y: number }[]) {
	let inside = false;
	for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
		const xi = points[i].x;
		const yi = points[i].y;
		const xj = points[j].x;
		const yj = points[j].y;
		if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
	}
	return inside;
}

function withZips(selected: Set<string>, zips: Iterable<string>, on: boolean) {
	const next = new Set(selected);
	for (const zip of zips) {
		if (on) next.add(zip);
		else next.delete(zip);
	}
	return next;
}

interface ZipCodePickerModalProps {
	initialZipCodes: string[];
	homeZipCode: string;
	onApply: (zipCodes: string[]) => void;
	closeModal: () => void;
}

export function ZipCodePickerModal({ initialZipCodes, homeZipCode, onApply, closeModal }: ZipCodePickerModalProps) {
	const [selected, setSelected] = useState(() => new Set(initialZipCodes));
	const [mode, setMode] = useState<PickerMode>('pick');
	const [filter, setFilter] = useState('');
	const [onlySelected, setOnlySelected] = useState(false);
	const [lassoHint, setLassoHint] = useState<string | null>(null);

	const svgRef = useRef<SVGSVGElement>(null);
	const lassoRef = useRef<SVGPathElement>(null);
	const lassoPoints = useRef<{ x: number; y: number }[] | null>(null);

	const home = ZIP_CODES_BY_ZIP[homeZipCode] ?? null;
	const isDrawing = mode === 'add';

	const toggleZip = (zip: string) => {
		setSelected(x => withZips(x, [zip], !x.has(zip)));
		setLassoHint(null);
	};

	const changeMode = (nextMode: PickerMode) => {
		setMode(nextMode);
		setLassoHint(null);
	};

	const toSvgPoint = (e: ReactPointerEvent<SVGSVGElement>) => {
		const svg = svgRef.current;
		const matrix = svg?.getScreenCTM();
		if (!svg || !matrix) return { x: 0, y: 0 };
		const point = svg.createSVGPoint();
		point.x = e.clientX;
		point.y = e.clientY;
		const transformed = point.matrixTransform(matrix.inverse());
		return { x: transformed.x, y: transformed.y };
	};

	const handlePointerDown = (e: ReactPointerEvent<SVGSVGElement>) => {
		if (!isDrawing) return;
		e.preventDefault();
		e.currentTarget.setPointerCapture(e.pointerId);
		lassoPoints.current = [toSvgPoint(e)];
		lassoRef.current?.setAttribute('opacity', '1');
	};

	const handlePointerMove = (e: ReactPointerEvent<SVGSVGElement>) => {
		const points = lassoPoints.current;
		if (!points) return;
		const point = toSvgPoint(e);
		const last = points[points.length - 1];
		if (Math.hypot(point.x - last.x, point.y - last.y) < 2.5) return;
		points.push(point);
		lassoRef.current?.setAttribute('d', 'M' + points.map(x => `${x.x.toFixed(1)},${x.y.toFixed(1)}`).join('L') + 'Z');
	};

	const clearLasso = () => {
		lassoPoints.current = null;
		lassoRef.current?.setAttribute('opacity', '0');
		lassoRef.current?.removeAttribute('d');
	};

	const handlePointerUp = () => {
		const points = lassoPoints.current;
		if (!points) return;
		if (points.length > 4) {
			const hits = ZIP_CODES.filter(x => isInsidePolygon(toX(x.lon), toY(x.lat), points)).map(x => x.zip);
			setSelected(x => withZips(x, hits, true));
			setLassoHint(
				hits.length
					? `${hits.length} ZIP${hits.length > 1 ? 's' : ''} added. Draw again or switch modes.`
					: 'That loop didn’t catch any ZIPs. Try drawing a wider circle.'
			);
		}
		clearLasso();
	};

	let pool = ZIP_CODES.filter(x => !onlySelected || selected.has(x.zip));
	if (filter) {
		const term = filter.toLowerCase();
		pool = pool.filter(x => x.zip.includes(term) || x.city.toLowerCase().includes(term) || x.county.toLowerCase().includes(term));
	}
	if (home) {
		pool = [...pool].sort((x, y) => x.county.localeCompare(y.county) || milesBetween(home, x) - milesBetween(home, y));
	}

	const countyGroups = pool.reduce<{ county: string; zips: ZipCodeEntry[] }[]>((groups, x) => {
		const last = groups[groups.length - 1];
		if (last?.county === x.county) last.zips.push(x);
		else groups.push({ county: x.county, zips: [x] });
		return groups;
	}, []);

	const selectedList = [...selected].sort();
	const countyCount = new Set(selectedList.map(x => ZIP_CODES_BY_ZIP[x]?.county).filter(Boolean)).size;

	return (
		<Modal
			title="Edit Zip Codes"
			closeModal={closeModal}
			size="xl"
			footer={
				<FooterButtonContainer>
					<Button onClick={closeModal} appearance="outline" intent="neutral" label="Cancel" />
					<Button onClick={() => onApply(selectedList)} label="Apply Zip Codes" />
				</FooterButtonContainer>
			}
		>
			<div className="flex flex-col gap-4 text-sm">
				<Subtitle className="text-sm">
					Mark the ZIP codes you&rsquo;re willing to take referrals in. Tap or draw on the map, or use the list to add them.
					{home ? ` Distances are measured from your address in ${home.zip} ${home.city}.` : ''}
				</Subtitle>

				<div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,376px)]">
					<div className="overflow-hidden rounded-lg border border-gray-200">
						<div className="border-b border-gray-200 px-3 py-2.5">
							<ButtonGroup options={MODE_OPTIONS} isSelected={x => x === mode} onSelect={changeMode} />
						</div>
						<div className="flex min-h-9 items-center border-b border-gray-200 px-3 py-2 text-xs text-gray-500">
							{lassoHint ?? MODE_HINTS[mode]}
						</div>
						<svg
							ref={svgRef}
							className={cn('block h-auto w-full bg-white', isDrawing && 'cursor-crosshair touch-none')}
							viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
							role="img"
							aria-label="ZIP codes across southwestern Pennsylvania"
							onPointerDown={handlePointerDown}
							onPointerMove={handlePointerMove}
							onPointerUp={handlePointerUp}
							onPointerCancel={clearLasso}
						>
							{COUNTY_LABELS.map(x => (
								<text
									key={x.county}
									x={toX(x.lon).toFixed(1)}
									y={(toY(x.lat) - (x.county === 'Allegheny' ? 34 : 0)).toFixed(1)}
									className="pointer-events-none fill-gray-400 text-[8.5px] font-bold uppercase tracking-widest"
									textAnchor="middle"
								>
									{x.county}
								</text>
							))}
							<g className={cn(isDrawing && 'pointer-events-none')}>
								{ZIP_CODES.map(x => {
									const on = selected.has(x.zip);
									const activate = () => toggleZip(x.zip);
									return (
										<g
											key={x.zip}
											className="cursor-pointer outline-none focus-visible:[&>circle]:stroke-primary-300 focus-visible:[&>circle]:stroke-2"
											tabIndex={0}
											role="button"
											onClick={activate}
											onKeyDown={e => {
												if (e.key === 'Enter' || e.key === ' ') {
													e.preventDefault();
													activate();
												}
											}}
										>
											<circle
												cx={toX(x.lon).toFixed(1)}
												cy={toY(x.lat).toFixed(1)}
												r={on ? 4.6 : 3.6}
												className={cn('stroke-white transition-colors', on ? 'fill-primary-500' : 'fill-gray-300')}
												strokeWidth={0.9}
											/>
											<title>{`${x.zip} · ${x.city} · ${x.county} Co.`}</title>
										</g>
									);
								})}
							</g>
							<path
								ref={lassoRef}
								className="fill-primary-500/10 stroke-primary-500"
								strokeWidth={1.6}
								strokeDasharray="4 3"
								opacity={0}
								pointerEvents="none"
							/>
							{home && (
								<g transform={`translate(${toX(home.lon).toFixed(1)},${toY(home.lat).toFixed(1)})`} pointerEvents="none">
									<circle r={7} className="fill-white stroke-polishedCopper" strokeWidth={2.2} />
									<circle r={2.4} className="fill-polishedCopper" />
									<title>{`Home · ${home.zip} ${home.city}`}</title>
								</g>
							)}
						</svg>
					</div>

					<div className="overflow-hidden rounded-lg border border-gray-200">
						<div className="flex flex-wrap gap-1.5 border-b border-gray-200 px-3 py-2.5">
							<Button
								size="chipRounded"
								appearance="outline"
								intent="neutral"
								label="All Allegheny"
								onClick={() =>
									setSelected(x =>
										withZips(
											x,
											ZIP_CODES.filter(y => y.county === 'Allegheny').map(y => y.zip),
											true
										)
									)
								}
							/>
							<Button
								size="chipRounded"
								appearance="outline"
								intent="neutral"
								label="Pittsburgh city"
								onClick={() => setSelected(x => withZips(x, PITTSBURGH_CITY_ZIPS, true))}
							/>
							<Button
								size="chipRounded"
								appearance="outline"
								intent="neutral"
								label={onlySelected ? 'Show all' : 'Show selected'}
								onClick={() => setOnlySelected(x => !x)}
							/>
							<Button
								size="chipRounded"
								appearance="outline"
								intent="neutral"
								label="Clear all"
								onClick={() => setSelected(new Set())}
							/>
						</div>
						<div className="border-b border-gray-200 p-2">
							<SearchInput placeholder="Search ZIP, city, or county" value={filter} onChange={x => setFilter(x.trim())} />
						</div>
						<div className="max-h-[440px] overflow-y-auto overscroll-contain">
							{!pool.length && <p className="px-3 py-5 text-gray-500">Nothing matches that search.</p>}
							{countyGroups.map(group => {
								const allOn = group.zips.every(x => selected.has(x.zip));
								return (
									<div key={group.county}>
										<div className="sticky top-0 z-[2] flex items-center justify-between border-b border-gray-200 bg-gray-50 py-1 pl-3 pr-1">
											<span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
												{group.county} County &middot; {group.zips.length}
											</span>
											<Button
												size="xs"
												appearance="ghost"
												intent="primary"
												label={allOn ? 'Remove all' : 'Add all'}
												onClick={() =>
													setSelected(x =>
														withZips(
															x,
															group.zips.map(y => y.zip),
															!allOn
														)
													)
												}
											/>
										</div>
										{group.zips.map(x => {
											const on = selected.has(x.zip);
											return (
												<div
													key={x.zip}
													className={cn(
														'flex items-center border-b border-gray-100 pr-3 hover:bg-gray-50',
														on && 'bg-primary-50'
													)}
												>
													<Checkbox
														size="small"
														label={`<span class="inline-block w-12 font-mono font-medium">${x.zip}</span>${x.city}`}
														isSelected={on}
														onChange={() => toggleZip(x.zip)}
														className="flex-1 px-3 py-0"
													/>
													{home && (
														<span className="shrink-0 font-mono text-xs text-gray-500">
															{milesBetween(home, x).toFixed(0)} mi
														</span>
													)}
												</div>
											);
										})}
									</div>
								);
							})}
						</div>
					</div>
				</div>

				<div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
					<div className="mb-2 flex items-center justify-between">
						<span className="text-xs font-semibold uppercase tracking-wide text-gray-600">Your included ZIP codes</span>
						<span className="text-xs text-gray-500">
							<b className="font-mono text-gray-900">{selectedList.length}</b> ZIPs &middot;{' '}
							<b className="font-mono text-gray-900">{countyCount}</b> counties
						</span>
					</div>
					<div className="max-h-32 overflow-y-auto break-all rounded-md border border-gray-200 bg-white px-3 py-2 font-mono text-xs leading-relaxed text-gray-800">
						{selectedList.length ? selectedList.join(', ') : <span className="text-gray-400">No ZIP codes selected yet.</span>}
					</div>
				</div>
			</div>
		</Modal>
	);
}
